import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlarmClock, Timer, Watch, Plus, Trash2, Play, Pause, RotateCcw,
  Flag, Radio, BellRing, Minus
} from 'lucide-react'
import { vibrate } from '../utils/appPrefs'
import './ToolsPage.css'

/* =====================================================================
   OUTILS — Réveil, Minuteur, Chronomètre
   - Le réveil peut lancer la radio (le même lecteur que la page Radio)
   - Minuteur et chrono continuent si on change de page
   ===================================================================== */

const pad = (n) => String(n).padStart(2, '0')
const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
const DAY_NAMES = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche']

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch { return fallback }
}
const write = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* rien */ }
}

const playTones = (freqs, step = 0.22, length = 0.3) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      osc.connect(g); g.connect(ctx.destination)
      osc.frequency.value = f
      const t = ctx.currentTime + i * step
      g.gain.setValueAtTime(0.3, t)
      g.gain.exponentialRampToValueAtTime(0.001, t + length)
      osc.start(t); osc.stop(t + length)
    })
    setTimeout(() => ctx.close(), (freqs.length * step + length) * 1000 + 300)
  } catch { /* audio indisponible */ }
}
const alarmBeep = () => playTones([880, 660, 880, 660, 880, 660], 0.4, 0.3)
const timerDoneSound = () => playTones([523, 659, 784, 1047], 0.18, 0.35)

const fmtTimer = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`
}

const fmtChrono = (ms) => {
  const cs = Math.floor((ms % 1000) / 10)
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)
  const main = h > 0 ? `${h}:${pad(m % 60)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`
  return { main, cs: pad(cs) }
}

const describeDays = (days) => {
  if (!days.length) return 'Une seule fois'
  if (days.length === 7) return 'Tous les jours'
  const sorted = [...days].sort()
  if (sorted.join() === '0,1,2,3,4') return 'En semaine'
  if (sorted.join() === '5,6') return 'Le week-end'
  return sorted.map((d) => DAY_NAMES[d].slice(0, 3) + '.').join(' ')
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`t-switch ${checked ? 'is-on' : ''}`}
      onClick={() => { vibrate(8); onChange(!checked) }}
    >
      <span />
    </button>
  )
}

function Ring({ progress, size = 250, stroke = 12, children }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  return (
    <div className="t-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="#d4af37" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0, Math.min(1, progress)))}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset .25s linear' }}
        />
      </svg>
      <div className="t-ring__center">{children}</div>
    </div>
  )
}

// =====================================================================
// RÉVEIL
// =====================================================================
function AlarmTab({ globalAudio }) {
  const [alarms, setAlarms] = useState(() => {
    const saved = read('johnnyToolsAlarms', null)
    if (Array.isArray(saved) && saved.length) {
      return saved.map((a) => ({ ...a, ringing: false, days: Array.isArray(a.days) ? a.days : [] }))
    }
    return [{ id: Date.now(), time: '07:00', label: 'Réveil du matin', enabled: false, withRadio: true, days: [0, 1, 2, 3, 4] }]
  })
  const [now, setNow] = useState(() => new Date())
  const [ringing, setRinging] = useState(null)
  const alarmsRef = useRef(alarms)
  const audioRef = useRef(globalAudio)
  const lastStampRef = useRef('')
  const beepLoopRef = useRef(null)
  const startedRadioRef = useRef(false)

  audioRef.current = globalAudio
  useEffect(() => { alarmsRef.current = alarms; write('johnnyToolsAlarms', alarms) }, [alarms])

  const update = (id, patch) => setAlarms((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)))
  const remove = (id) => setAlarms((prev) => prev.filter((a) => a.id !== id))
  const add = () => {
    if (alarms.length >= 8) return
    vibrate(10)
    setAlarms((prev) => [...prev, { id: Date.now(), time: '08:00', label: '', enabled: true, withRadio: true, days: [] }])
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {})
    }
  }

  const stopSound = () => {
    if (beepLoopRef.current) { clearInterval(beepLoopRef.current); beepLoopRef.current = null }
  }

  const startRinging = useCallback((alarm) => {
    setRinging(alarm)
    vibrate([300, 150, 300])
    const audio = audioRef.current
    if (alarm.withRadio && audio?.togglePlay) {
      if (!audio.isPlaying) {
        startedRadioRef.current = true
        audio.togglePlay()
      }
    } else {
      alarmBeep()
      beepLoopRef.current = setInterval(alarmBeep, 3000)
    }
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(alarm.label || 'Réveil 100% Johnny', {
          body: `Il est ${alarm.time}`, icon: '/images/logo.png', tag: `alarm-${alarm.id}`
        })
      } catch { /* rien */ }
    }
  }, [])

  // Horloge + vérification (1 fois par seconde, sans effet de bord
  // dans les mises à jour d'état : le réveil ne sonne qu'une fois)
  useEffect(() => {
    const tick = () => {
      const d = new Date()
      setNow(d)
      const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
      const stamp = `${d.toDateString()} ${hm}`
      if (stamp === lastStampRef.current) return
      lastStampRef.current = stamp
      const dow = (d.getDay() + 6) % 7

      const due = alarmsRef.current.find((a) => {
        if (!a.enabled) return false
        if (a.snoozeTo) return a.snoozeTo === hm
        if (a.time !== hm) return false
        return !a.days.length || a.days.includes(dow)
      })

      if (due) {
        startRinging(due)
        setAlarms((prev) => prev.map((a) => (a.id === due.id
          ? { ...a, snoozeTo: null, enabled: a.days.length > 0 }
          : a)))
      }
    }
    tick()
    const iv = setInterval(tick, 1000)
    return () => clearInterval(iv)
  }, [startRinging])

  useEffect(() => () => stopSound(), [])

  const stop = () => {
    stopSound()
    const audio = audioRef.current
    if (startedRadioRef.current && audio?.isPlaying) audio.togglePlay()
    startedRadioRef.current = false
    setRinging(null)
  }

  const snooze = () => {
    const d = new Date(Date.now() + 5 * 60000)
    update(ringing.id, { enabled: true, snoozeTo: `${pad(d.getHours())}:${pad(d.getMinutes())}` })
    stop()
  }

  const activeCount = useMemo(() => alarms.filter((a) => a.enabled).length, [alarms])

  return (
    <div className="t-stack">
      <div className="t-clock">
        <div className="t-clock__time">{pad(now.getHours())}:{pad(now.getMinutes())}</div>
        <div className="t-clock__date">
          {now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <div className="t-clock__next">
          {activeCount ? `${activeCount} réveil${activeCount > 1 ? 's' : ''} activé${activeCount > 1 ? 's' : ''}` : 'Aucun réveil activé'}
        </div>
      </div>

      {alarms.map((alarm) => (
        <article key={alarm.id} className={`t-alarm ${alarm.enabled ? 'is-on' : ''}`}>
          <div className="t-alarm__row">
            <input
              className="t-alarm__time"
              type="time"
              value={alarm.time}
              onChange={(e) => update(alarm.id, { time: e.target.value, enabled: true, snoozeTo: null })}
              aria-label="Heure du réveil"
            />
            <Switch
              checked={alarm.enabled}
              onChange={(v) => update(alarm.id, { enabled: v, snoozeTo: null })}
              label={alarm.enabled ? 'Désactiver ce réveil' : 'Activer ce réveil'}
            />
          </div>

          <input
            className="t-alarm__label"
            type="text"
            placeholder="Nom du réveil (facultatif)"
            value={alarm.label}
            maxLength={40}
            onChange={(e) => update(alarm.id, { label: e.target.value })}
          />

          <div className="t-alarm__days" role="group" aria-label="Jours de répétition">
            {DAYS.map((d, i) => {
              const on = alarm.days.includes(i)
              return (
                <button
                  key={i}
                  type="button"
                  className={`j-chip t-day ${on ? 'is-on' : ''}`}
                  aria-pressed={on}
                  aria-label={DAY_NAMES[i]}
                  onClick={() => update(alarm.id, {
                    days: on ? alarm.days.filter((x) => x !== i) : [...alarm.days, i]
                  })}
                >
                  {d}
                </button>
              )
            })}
          </div>

          <div className="t-alarm__foot">
            <span className="t-alarm__repeat">
              {alarm.snoozeTo ? `Répétition à ${alarm.snoozeTo}` : describeDays(alarm.days)}
            </span>
            <div className="t-alarm__opts">
              <button
                type="button"
                className={`t-pill ${alarm.withRadio ? 'is-on' : ''}`}
                onClick={() => update(alarm.id, { withRadio: !alarm.withRadio })}
                aria-pressed={alarm.withRadio}
              >
                {alarm.withRadio ? <Radio size={16} /> : <BellRing size={16} />}
                {alarm.withRadio ? 'Radio' : 'Sonnerie'}
              </button>
              <button type="button" className="t-icon" onClick={() => remove(alarm.id)} aria-label="Supprimer ce réveil">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        </article>
      ))}

      {alarms.length < 8 && (
        <button type="button" className="t-add" onClick={add}>
          <Plus size={20} /> Ajouter un réveil
        </button>
      )}

      <p className="t-note">
        Le réveil sonne tant que l'application reste ouverte, écran allumé.
        Pour être sûr d'être réveillé téléphone verrouillé, gardez aussi l'alarme du téléphone.
      </p>

      {ringing && (
        <div className="t-ringing" role="alertdialog" aria-label="Réveil en cours">
          <div className="t-ringing__icon"><AlarmClock size={56} /></div>
          <div className="t-ringing__time">{ringing.time}</div>
          <div className="t-ringing__label">{ringing.label || "Debout, c'est l'heure de Johnny"}</div>
          <button type="button" className="t-btn t-btn--gold t-btn--xl" onClick={stop}>Arrêter</button>
          <button type="button" className="t-btn t-btn--ghost" onClick={snooze}>Répéter dans 5 min</button>
        </div>
      )}
    </div>
  )
}

// =====================================================================
// MINUTEUR
// =====================================================================
const PRESETS = [1, 3, 5, 10, 15, 30, 45, 60]

function TimerTab() {
  const [saved] = useState(() => read('johnnyToolsTimer', null))
  const [total, setTotal] = useState(saved?.total ?? 5 * 60000)
  const [endAt, setEndAt] = useState(saved?.endAt ?? null)
  const [remaining, setRemaining] = useState(saved?.remaining ?? saved?.total ?? 5 * 60000)
  const [done, setDone] = useState(false)
  const running = Boolean(endAt)

  useEffect(() => { write('johnnyToolsTimer', { total, endAt, remaining }) }, [total, endAt, remaining])

  useEffect(() => {
    if (!endAt) return undefined
    const tick = () => {
      const rem = endAt - Date.now()
      if (rem <= 0) {
        setRemaining(0); setEndAt(null); setDone(true)
        timerDoneSound(); vibrate([400, 200, 400])
      } else {
        setRemaining(rem)
      }
    }
    tick()
    const iv = setInterval(tick, 250)
    return () => clearInterval(iv)
  }, [endAt])

  const choose = (min) => {
    vibrate(8)
    const ms = min * 60000
    setTotal(ms); setRemaining(ms); setEndAt(null); setDone(false)
  }

  const adjust = (deltaMin) => {
    vibrate(8)
    if (running) {
      setEndAt((e) => Math.max(Date.now() + 1000, e + deltaMin * 60000))
      setTotal((t) => Math.max(60000, t + deltaMin * 60000))
    } else {
      const ms = Math.max(60000, Math.min(24 * 3600000, remaining + deltaMin * 60000))
      setTotal(ms); setRemaining(ms); setDone(false)
    }
  }

  const start = () => { if (remaining > 0) { vibrate(12); setDone(false); setEndAt(Date.now() + remaining) } }
  const pause = () => { vibrate(8); setRemaining(Math.max(0, endAt - Date.now())); setEndAt(null) }
  const reset = () => { vibrate(8); setEndAt(null); setRemaining(total); setDone(false) }

  return (
    <div className="t-stack t-center">
      <Ring progress={total ? remaining / total : 0}>
        <div className={`t-big ${done ? 't-big--done' : ''}`}>{done ? 'Terminé' : fmtTimer(remaining)}</div>
        {!done && <div className="t-sub">sur {fmtTimer(total)}</div>}
      </Ring>

      <div className="t-adjust">
        <button type="button" className="t-icon t-icon--lg" onClick={() => adjust(-1)} aria-label="Retirer une minute" disabled={!running && remaining <= 60000}>
          <Minus size={22} />
        </button>
        <span>1 minute</span>
        <button type="button" className="t-icon t-icon--lg" onClick={() => adjust(1)} aria-label="Ajouter une minute">
          <Plus size={22} />
        </button>
      </div>

      {!running && (
        <div className="t-presets" role="group" aria-label="Durées rapides">
          {PRESETS.map((m) => (
            <button key={m} type="button" className={`j-chip t-preset ${total === m * 60000 ? 'is-on' : ''}`} onClick={() => choose(m)}>
              {m} min
            </button>
          ))}
        </div>
      )}

      <div className="t-controls">
        <button type="button" className="t-btn t-btn--ghost" onClick={reset} disabled={!running && remaining === total && !done}>
          <RotateCcw size={20} /> Remettre
        </button>
        {running ? (
          <button type="button" className="t-btn t-btn--gold t-btn--main" onClick={pause}>
            <Pause size={24} fill="currentColor" strokeWidth={0} /> Pause
          </button>
        ) : (
          <button type="button" className="t-btn t-btn--gold t-btn--main" onClick={done ? reset : start}>
            <Play size={24} fill="currentColor" strokeWidth={0} /> {done ? 'Recommencer' : remaining < total ? 'Reprendre' : 'Démarrer'}
          </button>
        )}
      </div>
    </div>
  )
}

// =====================================================================
// CHRONOMÈTRE
// =====================================================================
function ChronoTab() {
  const [saved] = useState(() => read('johnnyToolsChrono', null))
  const [startAt, setStartAt] = useState(saved?.startAt ?? null)
  const [accum, setAccum] = useState(saved?.accum ?? 0)
  const [laps, setLaps] = useState(saved?.laps ?? [])
  const [, force] = useState(0)
  const running = Boolean(startAt)
  const elapsed = accum + (startAt ? Date.now() - startAt : 0)

  useEffect(() => { write('johnnyToolsChrono', { startAt, accum, laps }) }, [startAt, accum, laps])

  // ~30 images/s seulement pendant qu'il tourne (avant : 100/s)
  useEffect(() => {
    if (!running) return undefined
    let raf = 0
    let last = 0
    const loop = (t) => {
      if (t - last > 33) { last = t; force((n) => n + 1) }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [running])

  const start = () => { vibrate(12); setStartAt(Date.now()) }
  const pause = () => { vibrate(8); setAccum(elapsed); setStartAt(null) }
  const reset = () => { vibrate(8); setStartAt(null); setAccum(0); setLaps([]) }
  const lap = () => {
    vibrate(8)
    const prev = laps[0]?.at ?? 0
    setLaps([{ n: laps.length + 1, at: elapsed, split: elapsed - prev }, ...laps])
  }

  const splits = laps.map((l) => l.split)
  const best = laps.length > 1 ? Math.min(...splits) : null
  const worst = laps.length > 1 ? Math.max(...splits) : null
  const t = fmtChrono(elapsed)

  return (
    <div className="t-stack t-center">
      <Ring progress={(elapsed % 60000) / 60000}>
        <div className="t-big">{t.main}<small>,{t.cs}</small></div>
        <div className="t-sub">{laps.length ? `${laps.length} tour${laps.length > 1 ? 's' : ''}` : 'Chronomètre'}</div>
      </Ring>

      <div className="t-controls">
        {running ? (
          <button type="button" className="t-btn t-btn--ghost" onClick={lap}><Flag size={20} /> Tour</button>
        ) : (
          <button type="button" className="t-btn t-btn--ghost" onClick={reset} disabled={elapsed === 0}>
            <RotateCcw size={20} /> Remettre
          </button>
        )}
        {running ? (
          <button type="button" className="t-btn t-btn--gold t-btn--main" onClick={pause}>
            <Pause size={24} fill="currentColor" strokeWidth={0} /> Pause
          </button>
        ) : (
          <button type="button" className="t-btn t-btn--gold t-btn--main" onClick={start}>
            <Play size={24} fill="currentColor" strokeWidth={0} /> {elapsed ? 'Reprendre' : 'Démarrer'}
          </button>
        )}
      </div>

      {laps.length > 0 && (
        <ol className="t-laps">
          {laps.map((l) => {
            const f = fmtChrono(l.split)
            const tag = l.split === best ? 'best' : l.split === worst ? 'worst' : ''
            return (
              <li key={l.n} className={tag ? `is-${tag}` : ''}>
                <span>Tour {l.n}</span>
                <span className="t-laps__split">{f.main},{f.cs}</span>
                <span className="t-laps__total">{fmtChrono(l.at).main}</span>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

const TABS = [
  { id: 'alarm', label: 'Réveil', icon: AlarmClock },
  { id: 'timer', label: 'Minuteur', icon: Timer },
  { id: 'chrono', label: 'Chrono', icon: Watch }
]

export default function ToolsPage({ globalAudio }) {
  const [tab, setTab] = useState(() => {
    try { return sessionStorage.getItem('johnnyToolsTab') || 'alarm' } catch { return 'alarm' }
  })

  useEffect(() => {
    try { sessionStorage.setItem('johnnyToolsTab', tab) } catch { /* rien */ }
  }, [tab])

  return (
    <div className="page active tools-page">
      <div className="t-wrap">
        <header className="t-head">
          <h1>Outils</h1>
          <p>Réveillez-vous avec Johnny, minutez, chronométrez.</p>
        </header>

        <div className="t-tabs" role="tablist" aria-label="Outils">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`t-tab ${tab === id ? 'is-on' : ''}`}
              onClick={() => { vibrate(8); setTab(id) }}
            >
              <Icon size={20} /> {label}
            </button>
          ))}
        </div>

        <div role="tabpanel" className="t-panel">
          {tab === 'alarm' && <AlarmTab globalAudio={globalAudio} />}
          {tab === 'timer' && <TimerTab />}
          {tab === 'chrono' && <ChronoTab />}
        </div>
      </div>
    </div>
  )
}
